import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { KnowledgeSearchResult } from '../../../core/models/learn/knowledge-search-result';

/**
 * Blended search-results panel for the Knowledge Centre (#47). Renders the result set from the
 * api/v1/Knowledge/search endpoint — Learn AND FAQ in one list — each row carrying a TYPE BADGE
 * (Learn / FAQ) and a breadcrumb, linking learn→/learn/read/:slug and faq→/learn/faq/:slug.
 * Members-only results show a "Members" lock affordance and are NOT directly readable (no link).
 *
 * The panel RESERVES an inert AI-answer slot at the top: a placeholder the later Phase-2 Ask-AI
 * fills in place, so adding the grounded answer needs no re-layout. Persona is owned by the hub
 * (it re-runs the query); this component just renders whatever ranked set it is handed.
 *
 * A zero-result query renders the "captured as a content gap" state — the hub has already told the
 * API to record the gap; this is the reader-facing acknowledgement.
 */
@Component({
  selector: 'app-knowledge-search-results',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <section
      class="tw-max-w-[680px] tw-mx-auto tw-px-6 tw-mt-8"
      role="region"
      aria-label="Search results"
    >
      <!-- The grounded AI answer (#50) now fills the reserved slot above this panel, in the overlay
           (app-knowledge-ai-answer owns data-testid="kc-ai-answer-slot"). This panel is the blended
           keyword results beneath it. -->

      <!-- loading -->
      <div *ngIf="loading" class="tw-text-[14px] tw-text-[#9a9fa6] tw-py-8 tw-text-center">
        Searching…
      </div>

      <ng-container *ngIf="!loading">
        <!-- idle (no query yet): a calm hint, never the zero-result message -->
        <div
          *ngIf="!query"
          class="tw-text-[13.5px] tw-text-[#9a9fa6] tw-py-8 tw-text-center"
          data-testid="kc-search-idle"
        >
          Start typing to search Learn + Help &amp; FAQ.
        </div>

        <!-- zero-result (only once a query has been entered): captured content-gap state -->
        <div
          *ngIf="query && !results.length"
          class="tw-text-[14px] tw-text-[#6b6f76] tw-py-8 tw-text-center"
          data-testid="kc-search-empty"
        >
          No articles match <b>"{{ query }}"</b>.<br />
          We’ve noted this so we can write the answer you were looking for.
        </div>

        <!-- blended results: FAQ section then Learn section (one set) -->
        <ng-container *ngIf="results.length">
          <ng-container *ngTemplateOutlet="lane; context: { label: 'Help & FAQ', items: faqResults }"></ng-container>
          <ng-container *ngTemplateOutlet="lane; context: { label: 'Learn', items: learnResults }"></ng-container>
        </ng-container>
      </ng-container>

      <!-- one lane (a section label + its result rows) -->
      <ng-template #lane let-label="label" let-items="items">
        <ng-container *ngIf="items.length">
          <div
            class="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[.05em] tw-text-[#9aa0a6] tw-mt-5 tw-mb-2"
          >
            {{ label }} · {{ items.length }}
          </div>
          <ul class="tw-list-none tw-m-0 tw-p-0 tw-flex tw-flex-col tw-gap-2" data-testid="kc-search-results">
            <li *ngFor="let r of items">
              <!-- readable result → link; members-only → non-link locked row -->
              <a
                *ngIf="!r.gated"
                class="tw-no-underline"
                [routerLink]="linkFor(r)"
                [attr.data-testid]="'kc-search-result'"
              >
                <ng-container *ngTemplateOutlet="row; context: { r: r }"></ng-container>
              </a>
              <div
                *ngIf="r.gated"
                data-testid="kc-search-result"
                class="tw-cursor-default"
                [attr.aria-label]="r.title + ' — members only'"
              >
                <ng-container *ngTemplateOutlet="row; context: { r: r }"></ng-container>
              </div>
            </li>
          </ul>
        </ng-container>
      </ng-template>

      <!-- one result row: icon · title (+ members lock) · snippet · breadcrumb · type badge -->
      <ng-template #row let-r="r">
        <div
          class="tw-flex tw-items-start tw-gap-3 tw-bg-white tw-border tw-border-[#ececec] tw-rounded-[10px] tw-p-3 tw-transition-colors hover:tw-border-primary"
        >
          <span
            class="tw-flex-shrink-0 tw-w-8 tw-h-8 tw-rounded-[8px] tw-flex tw-items-center tw-justify-center"
            [ngClass]="r.type === 'learn' ? 'tw-bg-[#eef4ff] tw-text-[#2c6fd6]' : 'tw-bg-[#f0f7ee] tw-text-[#3d8a3d]'"
          >
            <svg
              *ngIf="r.type === 'learn'"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2z" /><path d="M8 7h7M8 11h7" />
            </svg>
            <svg
              *ngIf="r.type === 'faq'"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>

          <div class="tw-flex-1 tw-min-w-0">
            <div class="tw-flex tw-items-center tw-gap-2">
              <span class="tw-text-[14.5px] tw-font-semibold tw-text-[#1a1a1a] tw-truncate">{{ r.title }}</span>
              <span
                *ngIf="r.gated"
                class="tw-flex-shrink-0 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[.04em] tw-text-[#9a6a00] tw-bg-[#fff4d6] tw-rounded-full tw-px-2 tw-py-[1px]"
                >Members</span
              >
            </div>
            <div *ngIf="r.snippet" class="tw-text-[13px] tw-text-[#6b6f76] tw-mt-[2px] tw-line-clamp-2">
              {{ r.snippet }}
            </div>
            <div class="tw-text-[11.5px] tw-text-[#9aa0a6] tw-mt-[3px]">
              <b class="tw-text-[#6b6f76]">{{ r.type === 'learn' ? 'Learn' : 'Help & FAQ' }}</b>
              <ng-container *ngIf="r.productLine"> › {{ r.productLine | uppercase }}</ng-container>
            </div>
          </div>

          <span
            class="tw-flex-shrink-0 tw-self-center tw-text-[10.5px] tw-font-semibold tw-uppercase tw-tracking-[.04em] tw-rounded-full tw-px-2 tw-py-[2px]"
            [ngClass]="r.type === 'learn' ? 'tw-bg-[#eef4ff] tw-text-[#2c6fd6]' : 'tw-bg-[#f0f7ee] tw-text-[#3d8a3d]'"
          >
            {{ r.type === 'learn' ? 'Learn' : 'FAQ' }}
          </span>
        </div>
      </ng-template>
    </section>
  `,
})
export class KnowledgeSearchResultsComponent {
  @Input() results: KnowledgeSearchResult[] = [];
  @Input() query = '';
  @Input() loading = false;

  get faqResults(): KnowledgeSearchResult[] {
    return this.results.filter((r) => r.type === 'faq');
  }

  get learnResults(): KnowledgeSearchResult[] {
    return this.results.filter((r) => r.type === 'learn');
  }

  /** Route a readable (non-gated) result: learn→/learn/read/:slug, faq→/learn/faq/:slug. */
  linkFor(r: KnowledgeSearchResult): (string | undefined)[] {
    return r.type === 'learn' ? ['/learn/read', r.slug] : ['/learn/faq', r.slug];
  }
}
